/**
 * Activity Picker Modal.
 *
 * Opened when a Data Quality cell click resolves to multiple contributing
 * activities (e.g. two same-month entries for the same source). The user
 * picks one, the picker closes, and the parent opens the drawer for the
 * chosen activity. Each row lazily fetches its own activity record so the
 * picker can show period + quantity + co₂e without the parent having to
 * pre-load N activities.
 */

import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { Loader2 } from 'lucide-react';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { useGetActivityQuery } from '../../../api/emissions';
import { useEmissionsFormatter } from '../../../hooks/useEmissionsFormatter';

interface ActivityPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Contributing activity IDs from the bucket. */
  activityIds: string[];
  /** Asset's initiative id — used for each row's fetch. */
  initiativeId: string;
  /** Header copy ("Natural Gas — Mar '25"). */
  title: string;
  /** Called with the chosen activity id. Picker closes; parent opens the drawer. */
  onSelect: (activityId: string) => void;
}

export const ActivityPickerModal = ({
  isOpen,
  onClose,
  activityIds,
  initiativeId,
  title,
  onSelect,
}: ActivityPickerModalProps) => (
  <Modal isOpen={isOpen} toggle={onClose} centered size='md'>
    <ModalHeader toggle={onClose}>
      Activities — {title}
    </ModalHeader>
    <ModalBody className='tw:p-0'>
      <p className='tw:text-sm tw:text-muted-foreground tw:px-4 tw:pt-4 tw:mb-2'>
        {activityIds.length} activities contributed to this period. Select one to view its calculation.
      </p>
      <ul className='tw:list-none tw:m-0 tw:p-0'>
        {activityIds.map((id) => (
          <ActivityPickerRow
            key={id}
            activityId={id}
            initiativeId={initiativeId}
            onSelect={() => onSelect(id)}
          />
        ))}
      </ul>
    </ModalBody>
  </Modal>
);

interface ActivityPickerRowProps {
  activityId: string;
  initiativeId: string;
  onSelect: () => void;
}

const ActivityPickerRow = ({ activityId, initiativeId, onSelect }: ActivityPickerRowProps) => {
  const { formatCompact } = useEmissionsFormatter();
  const { data: activity, isLoading } = useGetActivityQuery({ initiativeId, activityId });

  const formatPeriod = (start?: string, end?: string): string => {
    if (!start || !end) return '';
    const s = new Date(start);
    const e = new Date(end);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return '';
    const fmt = (d: Date) => d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: '2-digit' });
    return `${fmt(s)} – ${fmt(e)}`;
  };

  return (
    <li>
      <button
        type='button'
        onClick={onSelect}
        className={cn(
          'tw:w-full tw:text-left tw:bg-transparent tw:border-0 tw:px-4 tw:py-3 tw:cursor-pointer',
          'tw:border-t tw:border-border tw:hover:bg-muted/50 tw:focus-visible:outline-none tw:focus-visible:bg-muted/50',
        )}
      >
        {isLoading || !activity ? (
          <div className='tw:flex tw:items-center tw:gap-2 tw:text-sm tw:text-muted-foreground'>
            <Loader2 className='tw:size-3.5 tw:animate-spin' />
            Loading…
          </div>
        ) : (
          <div className='tw:flex tw:items-center tw:justify-between tw:gap-3'>
            <div className='tw:min-w-0'>
              <div className='tw:text-sm tw:font-medium tw:truncate'>
                {activity.name || `${activity.quantity} ${activity.unit}`}
              </div>
              <div className='tw:text-xs tw:text-muted-foreground'>
                {formatPeriod(activity.periodStart, activity.periodEnd)}
                {activity.region ? ` · ${activity.region}` : ''}
              </div>
            </div>
            <div className='tw:text-sm tw:font-semibold tw:shrink-0'>
              {activity.calculatedCO2e !== undefined && activity.calculatedCO2e !== null
                ? formatCompact(activity.calculatedCO2e).display
                : '—'}
            </div>
          </div>
        )}
      </button>
    </li>
  );
};
